import React, {FC, Key, memo, ReactNode, RefObject, useCallback, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {sourceActivityIdAttribute} from '../../../../../../libs/shared/core/src/helpers/annotations/annotationHelpers';
import {isElement} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {usePrevious} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isShallowEqual} from '../../../../../../libs/shared/core/src/helpers/utilities/objectHelpers';
import {CommentAnnotationModel} from '../../../../../../libs/shared/core/src/models/api/annotationModel';
import {SelectionAnnotationTemplate} from '../../../../../../libs/shared/core/src/models/api/annotationTypes';
import {AttachmentModel} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {ConversationActionContextsEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {isBotTeammate} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {usePush} from '../../../../../../libs/shared/core/src/services/push/pushContext';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {NewPanelId, ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {LegacyInteractionParent} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {OnMentionClick} from '../../../../../../srcCommon/formatters/markdown/transforms/commonMarkdownTransformMentions';
import {isCommentOnlyAnEmoji} from '../../../../../../srcCommon/helpers/models/commentHelpers';
import {
  useIsActivityVisuallyHighlighted,
  useIsCommentVisuallyPinned,
} from '../../../../../../srcCommon/inboxes/viewer/conversation/timeline/viewerTimelineHelpers';
import {
  findSingleImageAttachment,
  hasUpdatedReactions,
  renderCommentTitle,
} from '../../../../../helpers/commentViewerHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {CommentModel, CommentStatusesEnum} from '../../../../../models/api/commentModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {EmojiMap} from '../../../../../services/emojis/emojis';
import {AnnotationsHighlightViewerContainer} from '../../../../common/annotation/annotationsHighlightViewerContainer';
import {useVariableHeightElement} from '../../../../common/scroll/useVariableHeightElement';
import {PrivateCommentInfoBubble} from '../../../../infoBubbles/privateCommentInfoBubble';
import {TeammateCardPopover, TeammateCardPreviewer} from '../../../../teammateCard/teammateCardPreviewer';
import {EditCommentComposerContainer} from '../../commentComposer/editCommentComposerContainer';
import {BasicViewerResnoozeContainer} from '../basic/basicViewerResnoozeContainer';
import {CommentBase} from './commentBase';
import {CommentReactionsProps} from './commentBaseReactions';
import {StyledEmojiDiv} from './commentBody';
import {CommentBodyWithUnfurls} from './commentBodyWithUnfurls';

/*
 * Constants.
 */

export const recentActivityThreshold = 10 * 60 * 1000; // 10 minutes.

/*
 * Props.
 */

export interface CommentViewerProps extends CommentReactionsProps {
  panelId: ViewerPanelId;
  comment: CommentModel;
  author: TeammateModel;
  cellsContext: CellsContext;
  mentionableAliases: ReadonlySet<string>;
  userAlias?: string;
  hasSameAuthorAsPrevious: boolean;
  isReferencingTheSameMessageAsPrevious: boolean;
  isLastSendingComment?: boolean;
  isFromUser?: boolean;
  isNewUnread?: boolean;
  isUnread?: boolean;
  annotations: ReadonlyArray<CommentAnnotationModel>;
  attachments: ReadonlyArray<AttachmentModel>;
  onReferencedActivityClick?: (arg: number) => void;
  editComment?: () => void;
  deleteComment?: () => void;
  retryComment?: () => void;
  previousActivity?: ActivityModel;
  isLastActivity?: boolean;
  canAddReactions?: boolean;
  onReplyClick?: () => void;
  referencedAuthor?: TeammateModel;
  referencedComment?: CommentModel;
  referencedRecipient?: VisualRecipientModel;
  userId?: number;
  isVisibleOnlyToUser?: boolean;
  isPinned?: boolean;
  pinnedByTeammate?: TeammateModel;
  togglePinComment?: () => void;
  jumpKey?: Key;
  isHighlighted?: boolean;
  isEditing?: boolean;
  emojiValidationMap: EmojiMap;
  namespace: string;
  isFirstComment?: boolean;
  onCreateAnnotatedComment?: (template: SelectionAnnotationTemplate) => void;
  jumpSourceActivityId?: number;
  conversationId: number;
  canceledReminder?: ReminderModel;
  conversationActionContext: ConversationActionContextsEnum | undefined;
  timelineVariation: TimelineVariationsEnum;
  activityId?: number;
  hasTeammateCardFF: boolean;
}

/*
 * Style.
 */

const StyledTimelineEmojiDiv = styled(StyledEmojiDiv)<{$hasSameAuthorAsPrevious: boolean}>`
  img {
    margin-top: ${(p) => (p.$hasSameAuthorAsPrevious ? '2px' : '-1px')};
  }
`;

/*
 * Component.
 */

export const CommentViewer: FC<CommentViewerProps> = memo((props: CommentViewerProps) => {
  const {
    comment,
    author,
    isUnread,
    previousActivity,
    hasSameAuthorAsPrevious,
    referencedAuthor,
    referencedComment,
    referencedRecipient,
    userId,
    mentionableAliases,
    userAlias,
    attachments,
    onReferencedActivityClick,
    isEditing,
    panelId,
    emojiValidationMap,
    deleteComment,
    isReferencingTheSameMessageAsPrevious,
    onCreateAnnotatedComment,
    annotations,
    jumpSourceActivityId,
    jumpKey,
    timelineVariation,
    cellsContext,
    hasTeammateCardFF,
  } = props;

  const {isConnected} = usePush();
  const viewerRef = useRef<HTMLDivElement>(null);

  // Special cases.
  const isSingleImageAttachment = Boolean(findSingleImageAttachment(comment));
  const isReply = Boolean(referencedAuthor);
  const isAnnotation = Boolean(comment.annotationUid);
  const isAllEmoji = isCommentOnlyAnEmoji(comment, isReply, attachments, emojiValidationMap);
  const hasError = comment.status === CommentStatusesEnum.FAILED;

  // Hide the timestamp if the delta between this comment and the (possible) previous comment
  // is by the same author and was less than 10min ago.
  const previousActivityDelta = previousActivity && comment.sentAt - previousActivity.date;
  const isPreviousActivityRecent = previousActivityDelta && previousActivityDelta < recentActivityThreshold;
  const shouldHideTimestamp = Boolean(hasSameAuthorAsPrevious && isPreviousActivityRecent);

  const shouldHideOperations = isEditing;

  // Hide the title if the comment doesn't reference somebody and the author is the same.
  const shouldHideTitle = hasSameAuthorAsPrevious && !isReply;

  const isBotUser = isBotTeammate(author);
  const useRenderAvatar = useCallback(() => {
    if (!hasTeammateCardFF || isBotUser) {
      return <TeammateAvatar teammate={author} size={VisualSizesEnum.LARGE} />;
    }
    return (
      <TeammateCardPreviewer panelId={panelId} teammate={author}>
        <TeammateAvatar teammate={author} size={VisualSizesEnum.LARGE} supportsHover hasDarkerHover />
      </TeammateCardPreviewer>
    );
  }, [author, panelId, hasTeammateCardFF, isBotUser]);
  const useRenderTitle = useCallback(
    () => renderCommentTitle(author, referencedAuthor, panelId, userId, isEditing),
    [author, referencedAuthor, panelId, userId, isEditing],
  );

  const renderOneBigEmoji: RendererOf<ReactNode> = useCallback(
    (emojiNode) => (
      <StyledTimelineEmojiDiv $hasSameAuthorAsPrevious={hasSameAuthorAsPrevious}>
        {emojiNode}
      </StyledTimelineEmojiDiv>
    ),
    [hasSameAuthorAsPrevious],
  );

  const isCommentVisuallyPinned = useIsCommentVisuallyPinned(props.isPinned ?? false);
  const isCommentVisuallyHighlighted = useIsActivityVisuallyHighlighted(props);
  const previousJumpKey = usePrevious(jumpKey);
  const previousJumpSourceActivityId = usePrevious(jumpSourceActivityId);

  // Animation where the highlighted section of an annotated comment turns blue when the user jumps to it.
  useEffect(() => {
    const jumpKeyChanged = previousJumpKey !== jumpKey;
    const jumpSourceActivityIdChanged = previousJumpSourceActivityId !== jumpSourceActivityId;
    const shouldJump = jumpSourceActivityId !== undefined && (jumpKeyChanged || jumpSourceActivityIdChanged);

    if (!shouldJump) {
      return;
    }

    const viewerElement = viewerRef.current;

    if (!viewerElement) {
      return;
    }

    viewerElement.querySelector(`[${sourceActivityIdAttribute}="${jumpSourceActivityId}"]`)?.scrollIntoView();

    viewerElement
      .querySelectorAll(`[${sourceActivityIdAttribute}="${jumpSourceActivityId}"]`)
      .forEach((el) => {
        el.classList.add('front_annotation_focused');
        el.addEventListener('animationend', endQuoteCommentJumpAnimation);
      });
  }, [jumpKey, jumpSourceActivityId]);

  // We inject the AnnotationsHighlightViewerContainer as a prop to comment body, because the
  // AnnotationsHighlightViewerContainer itself instantiates CommentBody via the FloatingCommentDropdown.
  // Importing AnnotationsHighlightViewerContainer in the CommentViewer allows us to avoid the circular
  // dependency issue.
  const textWrapper = (child: ReactNode) =>
    maybeRenderAnnotationsHighlightViewerWrapper(
      child,
      panelId,
      comment,
      annotations,
      onCreateAnnotatedComment,
    );

  // Report changes in our height to the VariableHeightScroller when these values change.
  useVariableHeightElement(viewerRef, [props.reactions, props.isPinned, props.comment.unfurls, isEditing]);

  const [mentionPopoverState, setMentionPopoverState] = React.useState<
    {ref: RefObject<any>; alias: string; teammateId: number | undefined} | undefined
  >();

  const onMentionClick: OnMentionClick = useCallback(
    (alias: string, ref: RefObject<any>) => {
      const teammateAlias = alias?.startsWith('@') ? alias.slice(1) : alias;
      setMentionPopoverState({ref, alias: teammateAlias, teammateId: comment.mentionsMap[teammateAlias]});
    },
    [comment],
  );

  const renderBody = () => {
    if (isEditing && deleteComment) {
      return (
        <EditCommentComposerContainer
          panelId={panelId}
          conversationId={comment.conversationId}
          comment={comment}
          onDeleteClick={deleteComment}
          cellsContext={cellsContext}
        />
      );
    }

    return (
      <>
        <CommentBodyWithUnfurls
          comment={comment}
          annotations={annotations}
          referencedAuthor={referencedAuthor}
          referencedComment={referencedComment}
          referencedRecipient={referencedRecipient}
          mentionableAliases={mentionableAliases}
          userAlias={userAlias}
          attachments={attachments}
          shouldAlwaysUseMultiAttachmentStyle
          onReferencedActivityClick={onReferencedActivityClick}
          onMentionClick={hasTeammateCardFF ? onMentionClick : undefined}
          renderOneBigEmoji={renderOneBigEmoji}
          panelId={props.panelId}
          namespace={props.namespace}
          textWrapper={textWrapper}
          isReferencingTheSameMessageAsPrevious={
            hasSameAuthorAsPrevious && isReferencingTheSameMessageAsPrevious
          }
        />
        {mentionPopoverState && (
          <TeammateCardPopover
            sourceRef={mentionPopoverState.ref}
            onRequestClose={() => setMentionPopoverState(undefined)}
            panelId={panelId}
            teammateId={mentionPopoverState.teammateId}
            teammateAlias={mentionPopoverState.alias}
          />
        )}
      </>
    );
  };

  return (
    <LegacyInteractionParent interactionId={`comment-${comment.uid}`}>
      <CommentBase
        date={comment.sentAt}
        lastEditedAt={comment.lastEditedAt}
        renderAvatar={useRenderAvatar}
        renderTitle={useRenderTitle}
        shouldHideTitle={shouldHideTitle}
        shouldHideReplyArrow={isEditing}
        shouldHideTimestamp={shouldHideTimestamp}
        shouldHideOperations={shouldHideOperations}
        shouldHideReactions={isEditing}
        isFaded={isSendingStatus(comment.status) && !isConnected}
        isFromUser={props.isFromUser}
        isLastSendingOffline={props.isLastSendingComment && !isConnected}
        isNewUnread={props.isNewUnread}
        isUnread={isUnread}
        hasError={hasError}
        editComment={props.editComment}
        deleteComment={props.deleteComment}
        retryComment={props.retryComment}
        shouldHideBackground={!isReply && !isAnnotation && (isSingleImageAttachment || isAllEmoji)}
        reactions={props.reactions}
        canAddReactions={props.canAddReactions}
        onToggleReaction={props.onToggleReaction}
        onAddReaction={props.onAddReaction}
        userId={userId}
        isVisibleOnlyToUser={props.isVisibleOnlyToUser}
        onReplyClick={props.onReplyClick}
        isReply={isReply}
        viewerRef={viewerRef}
        isPinned={props.isPinned}
        pinnedByTeammate={props.pinnedByTeammate}
        togglePinComment={props.togglePinComment}
        hasSameAuthorAsPrevious={hasSameAuthorAsPrevious}
        interactionId={comment.uid}
        shouldRenderWithoutBubble={isEditing}
        isFirstComment={props.isFirstComment}
        wasJustPinned={isCommentVisuallyPinned}
        wasJustHighlighted={isCommentVisuallyHighlighted}
        timelineVariation={timelineVariation}
        activityId={props.activityId}
        panelId={props.panelId}
      >
        {renderBody()}
      </CommentBase>
      {props.isFromUser && props.isVisibleOnlyToUser && <PrivateCommentInfoBubble anchor={viewerRef} />}
      {maybeRenderResnoozeNotification(props)}
    </LegacyInteractionParent>
  );
}, customEqualityCheck);

function maybeRenderResnoozeNotification(props: CommentViewerProps) {
  const {canceledReminder, cellsContext, conversationId, conversationActionContext} = props;
  if (!conversationActionContext || !canceledReminder) {
    return null;
  }

  return (
    <BasicViewerResnoozeContainer
      canceledReminder={canceledReminder}
      cellsContext={cellsContext}
      conversationId={conversationId}
      conversationActionContext={conversationActionContext}
    />
  );
}

export function maybeRenderAnnotationsHighlightViewerWrapper(
  child: ReactNode,
  panelId: NewPanelId,
  comment: CommentModel,
  annotations: ReadonlyArray<CommentAnnotationModel>,
  onCreateAnnotatedComment?: (template: SelectionAnnotationTemplate) => void,
) {
  // This FF has an awkward name, but the inline annotation/highlight feature (message-annotations) is a prerequisite
  // for the quote-comment feature.
  const hasMessageAnnotations = true;

  return (
    <AnnotationsHighlightViewerContainer
      annotations={annotations}
      onCreateAnnotatedComment={onCreateAnnotatedComment}
      panelId={panelId}
      conversationId={comment.conversationId}
      hasMessageAnnotations={hasMessageAnnotations}
      isAnnotatingComment
    >
      {child}
    </AnnotationsHighlightViewerContainer>
  );
}

// If this function returns true, we'll skip a rerender.
function customEqualityCheck(prev: CommentViewerProps, next: CommentViewerProps) {
  // Rerender if the reactions have changed.
  if (hasUpdatedReactions(prev.reactions, next.reactions)) {
    return false;
  }

  return isShallowEqual(prev, next);
}

/*
 * Helpers.
 */

function isSendingStatus(status: CommentStatusesEnum) {
  return status === CommentStatusesEnum.PENDING || status === CommentStatusesEnum.SENDING;
}

function endQuoteCommentJumpAnimation(e: Event) {
  const {target} = e;
  if (!target) {
    return;
  }

  target.removeEventListener('animationend', endQuoteCommentJumpAnimation);
  if (!isElement(target)) {
    return;
  }

  target.classList.remove('front_annotation_focused');
}
